import merge from 'lodash/merge';
import DomainResource from './DomainResource';
import Coding from './types/Coding';
import CodeableConcept from './types/CodeableConcept';
import Identifier from './types/Identifier';
import Reference from './types/Reference';
import Period from './types/Period';
import EncounterParticipant from './EncounterParticipant';
import EncounterLocation from './EncounterLocation';
import EncounterStatusHistory from './EncounterStatusHistory';
import EncounterClassHistory from './EncounterClassHistory';
import EncounterDiagnosis from './EncounterDiagnosis';
import EncounterHospitalization from './EncounterHospitalization';
import EncounterHistory from './EncounterHistory';
import get from 'lodash/get';
import EncounterDischargeRationale from '@/fhirworks/EncounterDischargeRationale';
import { ENCOUNTER_STATUS_DISPLAY, ENCOUNTER_STATUS_DISPLAY_SHORT, ENCOUNTER_FHIR_TO_UI_STATUS, PROGRAM_FHIR_TO_UI_STATUS } from '@/common/episodeStatusConfig';

export default class Encounter extends DomainResource {
    static __className = 'Encounter';

    __transfer = null;

    __objectStructure = {
        identifier: [Identifier],
        status: String, // planned | arrived | triaged | in-progress | onleave | finished | cancelled | entered-in-error | unknown
        statusHistory: [EncounterStatusHistory],
        class: Coding,
        classHistory: [EncounterClassHistory],
        type: [CodeableConcept],
        serviceType: CodeableConcept,
        priority: CodeableConcept,
        subject: Reference,
        episodeOfCare: [Reference],
        basedOn: [Reference],
        participant: [EncounterParticipant],
        appointment: [Reference],
        period: Period,
        // length: Duration,
        reasonCode: [CodeableConcept],
        reasonReference: [Reference],
        diagnosis: [EncounterDiagnosis],
        account: [Reference],
        hospitalization: EncounterHospitalization,
        location: [EncounterLocation],
        serviceProvider: Reference,
        partOf: Reference,

        /**
         * first-class extensions
         */
        superbillStatus: String,
        superbill: Reference,
        officeCode: String,
        codeDescription: String,
        healthcareService: Reference,
        referralSource: Reference,
        transferOrigin: Reference,
        transferDestination: Reference,
        selfPay: Boolean,
        portalPacketSent: Boolean,
        portalPacketUpdate: Boolean,
        // Sent stays a boolean for records that predate the timestamp; sentDate carries the when.
        portalPacketSentDate: String,
        portalPacketOpened: String,
        // 'inPerson' for a packet completed with staff; absent means it goes to the portal.
        portalPacketDelivery: String,
        setting: String,
        mode: String,
        dischargeRationale: EncounterDischargeRationale,
        externalReferral: Object,
        eligibilityStatus: String,
        estPeriod: Period,
        statusReason: String,
        holdPeriod: Period,
        holdRationale: String,
        encounterHistory: [EncounterHistory],
        outcomeToolsId: String,
        administersMedications: Boolean,
    };

    constructor(constructJson, className = 'Encounter') {
        super(constructJson, className);

        this._parentObj = this;

        this.createAndPopulateStructure(this.__objectStructure, constructJson, this.__objectDefaults);

        // Look for the transfer property in the constructor json
        let transferProperty = get(constructJson, 'transfer', undefined);
        if (transferProperty) {
            if (transferProperty.origin) this.transferOrigin = transferProperty.origin;
            if (transferProperty.destination) this.transferDestination = transferProperty.destination;
        }

        this.originalObjJson = this.toJSON();
    }

    // *************** Extension PROPERTIES ***************
    get transfer() {
        if (!this.__transfer) {
            this.__transfer = {
                get origin() {
                    return this.parent.transferOrigin;
                },
                set origin(value) {
                    this.parent.transferOrigin = value;
                },
                get destination() {
                    return this.parent.transferDestination;
                },
                set destination(value) {
                    this.parent.transferDestination = value;
                },
                parent: this,
            };
        }

        return this.__transfer;
    }

    toJSON() {
        return merge(super.toJSON(this), this.getJsonForStructure(this.__objectStructure));
    }

    // *************** Custom Getters/Setters ***************
    get bnEncounterStatus() {
        // First check for special eligibility statuses
        if (this?.eligibilityStatus === 'TBD') return 'TBD';
        if (this?.eligibilityStatus === 'NotEligible') return 'NotEligible';

        // If no special eligibility status, use the FHIR status
        return this?.status || 'planned';
    }
    get bnEncounterStatusDisplay() {
        // First check for special eligibility statuses
        if (this?.eligibilityStatus === 'TBD') {
            return ENCOUNTER_STATUS_DISPLAY.ELIGIBILITY_NOT_DETERMINED;
        }
        if (this?.eligibilityStatus === 'NotEligible') {
            return ENCOUNTER_STATUS_DISPLAY.NOT_ELIGIBLE;
        }

        // If no special eligibility status, use the FHIR status
        const fhirStatus = this?.status || 'planned';
        return PROGRAM_FHIR_TO_UI_STATUS[fhirStatus] || ENCOUNTER_STATUS_DISPLAY.PLANNED;
    }
}
